import React, { useEffect } from 'react';
import {
    FormControl,
    FormHelperText,
    InputLabel,
    MenuItem,
    Select,
    Stack,
    Typography,
} from '@mui/material';
import { FormFieldProps } from './index';
import isArray from 'lodash/isArray';

import { useTheme } from '@mui/material/styles';
import { useTranslation } from '../../../contexts/TranslationContext';
import { useFormBlocks, useFormErrors } from '../../../contexts/FormContext';

import './icon-selection.css';

export default function IconSelection({
    name,
    label,
    required,
    disabled,
    value,
    choices,
    multiple,
    onChange,
    cssClassName,
    attr,
}: FormFieldProps) {
    const theme = useTheme();
    const { translate } = useTranslation();
    const { error } = useFormErrors(name);
    const { showBlocks } = useFormBlocks();

    useEffect(() => {
        if (attr?.['show-logic'] && attr['show-logic']?.[value]) {
            showBlocks(attr['show-logic'][value]);
        }
    }, []);

    const renderOption = (optionKey: string, optionIndex: number) => {
        const option = choices?.[optionKey];
        let selected = false;
        if (isArray(value)) {
            selected = value.includes(option);
        } else {
            selected = (value?.id ?? value) === option;
        }
        return (
            <MenuItem key={optionIndex} value={option} selected={selected}>
                <Stack direction="row" alignItems="center" spacing={2}>
                    <span
                        className={`mam-ico${option} ${theme.palette.mode}`}
                    />
                    <Typography>
                        {optionKey.includes('form')
                            ? translate(optionKey)
                            : `Icon #${optionKey.replace('icon.', '')}`}
                    </Typography>
                </Stack>
            </MenuItem>
        );
    };

    const onValueChange = (newValue: any) => {
        if (attr?.['show-logic'] && attr['show-logic']?.[newValue]) {
            showBlocks(attr['show-logic'][newValue]);
        }
        onChange(newValue);
    };

    return (
        <FormControl fullWidth className={cssClassName}>
            <Stack spacing={0.5}>
                <InputLabel
                    sx={{ lineHeight: 1, color: 'text.secondary', mt: 0.5 }}
                    id={`form-field-label-${name}`}>
                    {translate(label)}
                </InputLabel>
                {choices ? (
                    <Select
                        labelId={`form-field-label-${name}`}
                        name={name}
                        required={required}
                        disabled={disabled}
                        onChange={event => onValueChange(event.target.value)}
                        multiple={multiple ?? false}
                        error={Boolean(error)}
                        value={value ? value : multiple ? [] : ''}
                        label={translate(label)}>
                        {Object.keys(choices).map(renderOption)}
                    </Select>
                ) : (
                    <Select
                        labelId={'form-field-label-select'}
                        id={'empty-select'}
                        name={'empty-select'}
                        required={required}
                        disabled={disabled}
                        error={Boolean(error)}
                        value={value}
                        label={translate('form.general.empty_select')}>
                        <MenuItem key={'empty'} value={''} selected>
                            {translate('form.general.empty_select')}
                        </MenuItem>
                    </Select>
                )}
                {Boolean(error) && (
                    <FormHelperText error id={name + '-error'}>
                        {translate(error ?? 'error')}
                    </FormHelperText>
                )}
            </Stack>
        </FormControl>
    );
}
